// src/features/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import instance from "../utils/api_instance.util";
import { errorToastify, successToastify } from "../utils/toastify.util";
import { listFailureMessage } from "../utils/apiError.util";

const initialState = {
  isSpin: false,
  isBtnLoad: false, // ✅ used by your UI buttons

  userDetails: null,

  // ---------------- USERS (existing) ----------------
  userList: [],
  listError: "",
  total: 0,
  offset: 0,
  page: 1, // 1-based
  totalPages: 1,
  limit: 10,

  searchText: "",
  statusFilter: "", // "", "1", "0"
  startDate: "",
  endDate: "",

  flag: null,
  formData: {},
  deleteUserId: null,

  // ---------------- ROLES (new, isolated) ----------------
  roleList: [],
  roleError: "",
  roleTotal: 0,
  rolePage: 1,
  roleTotalPages: 1,
  roleLimit: 10,

  roleFlag: null,
  roleFormData: {
    id: "",
    role: "",
    description: "",
    menuIds: [],
  },
  deleteRoleId: null,

  // ---------------- MENUS (for role create/edit) ----------------
  menuList: [],
  isMenuSpin: false,
  usertype: "user", // ✅ "user" | "admin"
  plans: [],
  sortBy: "id",
  sortOrder: "DESC",
};

// ---------------- USERS THUNKS (unchanged) ----------------
export const getUserDetails = createAsyncThunk("user/details", async () => {
  let staff = {};
  try {
    staff = JSON.parse(localStorage.getItem("staff") || "{}");
  } catch (err) {
    staff = {};
  }
  const permissions = Array.isArray(staff.permissions) ? staff.permissions : [];
  return {
    success: true,
    result: {
      name: staff.name || "",
      email: staff.email || "",
      roleid: permissions.includes("hunts.publish") ? "1" : "0",
    },
  };
});

export const getUserList = createAsyncThunk(
  "user/list",
  async (params, { getState }) => {
    try {
      const state = getState().user;

      const searchText =
        params?.searchText !== undefined ? params.searchText : state.searchText;

      const statusFilter =
        params?.user_status !== undefined
          ? params.user_status
          : state.statusFilter;
      const startDate =
        params?.startDate !== undefined ? params.startDate : state.startDate;
      const endDate =
        params?.endDate !== undefined ? params.endDate : state.endDate;

      const limit =
        params?.limit !== undefined ? Number(params.limit) : Number(state.limit);

      // ✅ NEW
      const usertype = "user";

      const sortBy =
        params?.sortBy !== undefined ? params.sortBy : state.sortBy || "id";
      const sortOrder =
        params?.sortOrder !== undefined ? params.sortOrder : state.sortOrder || "DESC";
      const plans =
        params?.plans !== undefined ? params.plans : state.plans || [];

      const safeLimit = Math.max(1, Number.isFinite(limit) ? limit : 10);
      const offset =
        params?.offset !== undefined ? Number(params.offset) : Number(state.offset);
      const safeOffset = Math.max(0, Number.isFinite(offset) ? offset : 0);
      const safePage = Math.floor(safeOffset / safeLimit) + 1;

      const { data } = await instance.get("/api/v1/user/user-list", {
        params: {
          searchText: searchText?.trim() || "",
          user_status:
            statusFilter === 0 || statusFilter === 1 ? statusFilter : "",
          limit: safeLimit,
          offset: safeOffset,
          usertype,
          sortBy,
          sortOrder,
          ...(startDate && { start: startDate }),
          ...(endDate && { end: endDate }),
          ...(plans.length > 0 && { plans }),
        },
      });

      return {
        api: data,
        meta: {
          page: safePage,
          limit: safeLimit,
          offset: safeOffset,
          usertype,
          startDate,
          endDate,
          searchText,
          statusFilter,
          plans,
        },
      };
    } catch (err) {
      const status = err?.response?.status || 0;
      const serverMessage = err?.response?.data?.message || err?.response?.data?.mesasge || "";
      return {
        api: {
          success: false,
          message: listFailureMessage(status, serverMessage, "users"),
        },
        meta: {},
      };
    }
  }
);


const apiErrorMessage = (data, fallback) =>
  data?.message || data?.mesasge || fallback;

export const createUser = createAsyncThunk(
  "user/createUser",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post("/api/v1/user/create-userv2", payload);

      if (data?.success) {
        successToastify(apiErrorMessage(data, "User is created successfully"));
        const state = getState().user;
        await dispatch(
          getUserList({
            searchText: state.searchText,
            user_status: state.statusFilter,
            startDate: state.startDate,
            endDate: state.endDate,
            limit: state.limit,
            offset: 0,
            usertype: state.usertype || "user",
            plans: state.plans,
          })
        );
        return data;
      }

      const msg = apiErrorMessage(data, "Failed to create user");
      errorToastify(msg);
      return rejectWithValue({ message: msg, data });
    } catch (err) {
      const data = err?.response?.data;
      const msg = apiErrorMessage(data, "Failed to create user");
      errorToastify(msg);
      return rejectWithValue({ message: msg, data });
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (payload, { dispatch, getState }) => {
    try {
      const { data } = await instance.put(
        `/api/v1/user/user-updatev2/${payload.id}`,
        payload
      );

      if (data.success) {
        successToastify(apiErrorMessage(data, "User updated successfully"));

        const state = getState().user;
        await dispatch(
          getUserList({
            searchText: state.searchText,
            user_status: state.statusFilter,
            startDate: state.startDate,
            endDate: state.endDate,
            limit: state.limit,
            offset: state.offset,
            usertype: state.usertype || payload?.user_type || "user",
            plans: state.plans,
          })
        );
        return data;
      }

      const msg = apiErrorMessage(data, "Failed to update user");
      errorToastify(msg);
      return { ...data, success: false };
    } catch (err) {
      const { data } = err?.response || {};
      errorToastify(apiErrorMessage(data, "Failed to update user"));
      return { success: false, ...(data || {}) };
    }
  }
);


// new 10.02.2026 (unchanged behavior)
export const deleteUser = createAsyncThunk(
  "user/delete-user",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const body = {
        userid: payload?.userid ?? payload?.id,
      };
      const { data } = await instance.post(`/api/v1/user/delete-user`, body);

      if (data?.success) {
        successToastify(apiErrorMessage(data, "User deleted successfully"));
        const state = getState().user;
        await dispatch(
          getUserList({
            searchText: state.searchText,
            user_status: state.statusFilter,
            startDate: state.startDate,
            endDate: state.endDate,
            limit: state.limit,
            offset: state.offset,
            plans: state.plans,
          })
        );
        return data;
      }

      const msg = apiErrorMessage(data, "Failed to delete user");
      errorToastify(msg);
      return rejectWithValue({ message: msg, data });
    } catch (err) {
      const data = err?.response?.data;
      const msg = apiErrorMessage(data, "Failed to delete user");
      errorToastify(msg);
      return rejectWithValue({ message: msg, data });
    }
  }
);

// ---------------- ROLES THUNKS (fixed) ----------------

export const createRole = createAsyncThunk(
  "user/role/create",
  async (payload, { dispatch }) => {
    try {
      const { data } = await instance.post(`/api/v1/user/role/create`, payload);

      if (data.success) {
        successToastify("Role is created successfully");
        await dispatch(getRoleList()); // refresh role list
      }
      return data;
    } catch (err) {
      errorToastify("Failed to create role");
      throw err;
    }
  }
);

export const getRoleList = createAsyncThunk(
  "user/role/list",
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState().user;

      const searchText =
        params?.searchText !== undefined ? params.searchText : state.searchText;

      const limit =
        params?.limit !== undefined
          ? Number(params.limit)
          : Number(state.roleLimit || state.limit || 10);

      const page =
        params?.page !== undefined
          ? Number(params.page)
          : Number(state.rolePage || 1);

      const safeLimit = Math.max(1, Number.isFinite(limit) ? limit : 10);
      const safePage = Math.max(1, Number.isFinite(page) ? page : 1);
      const offset = (safePage - 1) * safeLimit;

      const payload = {
        searchText: searchText?.trim() || "",
        limit: safeLimit,
        offset,
      };

      // ✅ your role/list uses POST
      const { data } = await instance.post("/api/v1/user/role/list", payload);

      return {
        api: data,
        meta: { page: safePage, limit: safeLimit },
      };
    } catch (err) {
      const status = err?.response?.status || 0;
      const serverMessage = err?.response?.data?.message || err?.response?.data?.mesasge || "";
      return rejectWithValue({
        status,
        message: listFailureMessage(status, serverMessage, "roles"),
      });
    }
  }
);

// ✅ IMPORTANT: returns data (no success toast, no list refresh)
export const getRoleById = createAsyncThunk("user/role/get", async (payload) => {
  try {
    const { data } = await instance.post(`/api/v1/user/role/get`, payload);
    if (!data?.success) {
      errorToastify(data?.mesasge || "Failed to fetch role");
    }
    return data; // ✅ so RoleList.jsx can unwrap and build formData
  } catch (err) {
    const { data } = err?.response || {};
    errorToastify(data?.mesasge || "Failed to fetch role");
    throw err;
  }
});

export const updateRole = createAsyncThunk(
  "user/role/update",
  async (payload, { dispatch }) => {
    try {
      const { data } = await instance.post(`/api/v1/user/role/update`, payload);

      if (data.success) {
        successToastify("Role is updated successfully");
        await dispatch(getRoleList());
      }
      return data;
    } catch (err) {
      const { data } = err?.response || {};
      errorToastify(data?.mesasge || "Failed to update role");
      throw err;
    }
  }
);

export const deleteRole = createAsyncThunk(
  "user/role/delete",
  async (payload, { dispatch }) => {
    try {
      const { data } = await instance.post(`/api/v1/user/role/delete`, payload);

      if (data.success) {
        const msg = data?.mesasge || "Success";
        successToastify(msg);
        await dispatch(getRoleList());
      }
      return data;
    } catch (err) {
      const { data } = err?.response || {};
      errorToastify(data?.mesasge || "Failed to delete role");
      throw err;
    }
  }
);

// ---------------- MENUS THUNK (fixed + stores in state) ----------------

// ✅ IMPORTANT: returns data only (no success toast, no role refresh)
export const getAllMenus = createAsyncThunk(
  "user/getAllMenus",
  async (payload) => {
    try {
      const { data } = await instance.post(`/api/v1/user/getAllMenus`, payload);
      return data;
    } catch (err) {
      const { data } = err?.response || {};
      errorToastify(data?.mesasge || "Failed to fetch menus");
      throw err;
    }
  }
);

// ---------------- SLICE ----------------
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // ---------- USERS existing reducers ----------
    setFormData: (state, { payload }) => {
      state.formData = { ...state.formData, ...payload };
    },
    setFlag: (state, { payload }) => {
      state.flag = payload;
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
      state.offset = 0;
      state.page = 1;
    },
    setStatusFilter: (state, { payload }) => {
      state.statusFilter = payload;
      state.offset = 0;
      state.page = 1;
    },
    setLimit: (state, { payload }) => {
      const val = Number(payload) || 10;
      state.limit = Math.max(1, val);
      state.offset = 0;
      state.page = 1;
    },
    setPage: (state, { payload }) => {
      const val = Number(payload) || 1;
      state.page = Math.max(1, val);
      state.offset = (state.page - 1) * state.limit;
    },
    setDeleteUserId: (state, action) => {
      state.deleteUserId = action.payload;
    },
    clearDeleteUserId: (state) => {
      state.deleteUserId = null;
    },

    // ---------- ROLES (new) ----------
    setRoleFlag: (state, { payload }) => {
      state.roleFlag = payload; // "add" | "update"
    },
    setRoleFormData: (state, { payload }) => {
      state.roleFormData = { ...state.roleFormData, ...payload };
    },
    setDeleteRoleId: (state, { payload }) => {
      state.deleteRoleId = payload;
    },
    clearDeleteRoleId: (state) => {
      state.deleteRoleId = null;
    },
    setUsertype: (state, { payload }) => {
      state.usertype = payload || "user";
      state.offset = 0;
      state.page = 1;
    },


    // optional role pagination helpers (won't break anything)
    setRoleLimit: (state, { payload }) => {
      const val = Number(payload) || 10;
      state.roleLimit = Math.max(1, val);
      state.rolePage = 1;
    },
    setRolePage: (state, { payload }) => {
      const val = Number(payload) || 1;
      state.rolePage = Math.max(1, val);
    },
  },
  extraReducers: (builder) => {
    // ---------------- USERS (existing behavior preserved) ----------------
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      if (action.payload?.success) {
        state.userDetails = action.payload.result;
      } else {
        state.userDetails = null;
      }
    });

    builder.addCase(getUserList.fulfilled, (state, action) => {
      const { api, meta } = action.payload || {};
      if (api?.success && api?.result) {
        const { data = [], total = 0, limit = meta?.limit || state.limit } =
          api.result || {};

        state.userList = data;
        state.listError = "";
        state.total = Number(total) || 0;
        state.limit = Number(limit) || state.limit;
        state.offset = Number(meta?.offset) || 0;
        state.searchText = meta?.searchText ?? state.searchText;
        state.statusFilter = meta?.statusFilter ?? state.statusFilter;
        state.startDate = meta?.startDate ?? state.startDate;
        state.endDate = meta?.endDate ?? state.endDate;
        state.usertype = meta?.usertype || state.usertype;
        if (meta?.plans !== undefined) {
          state.plans = Array.isArray(meta.plans) ? meta.plans : [];
        }

        state.totalPages = Math.max(
          1,
          Math.ceil((Number(total) || 0) / (Number(state.limit) || 10))
        );
        state.page = meta?.page || state.page;
      } else {
        state.userList = [];
        state.total = 0;
        state.totalPages = 1;
        state.listError = api?.message || "";
      }
    });

    builder.addCase(updateUser.pending, (state) => {
      state.isSpin = true;
      state.isBtnLoad = true;
    });
    builder.addCase(updateUser.fulfilled, (state) => {
      state.isSpin = false;
      state.isBtnLoad = false;
    });
    builder.addCase(updateUser.rejected, (state) => {
      state.isSpin = false;
      state.isBtnLoad = false;
    });

    builder.addCase(createUser.pending, (state) => {
      state.isSpin = true;
      state.isBtnLoad = true;
    });
    builder.addCase(createUser.fulfilled, (state) => {
      state.isSpin = false;
      state.isBtnLoad = false;
    });
    builder.addCase(createUser.rejected, (state) => {
      state.isSpin = false;
      state.isBtnLoad = false;
    });

    builder.addCase(deleteUser.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(deleteUser.fulfilled, (state) => {
      state.isBtnLoad = false;
    });
    builder.addCase(deleteUser.rejected, (state) => {
      state.isBtnLoad = false;
    });

    // ---------------- ROLES ----------------
    builder.addCase(getRoleList.pending, (state) => {
      state.isSpin = true;
      state.roleError = "";
    });
    builder.addCase(getRoleList.rejected, (state, action) => {
      state.isSpin = false;
      state.roleList = [];
      state.roleTotal = 0;
      state.roleError = action.payload?.message || "Unable to load roles.";
    });
    builder.addCase(getRoleList.fulfilled, (state, action) => {
      state.isSpin = false;
      state.roleError = "";

      const { api, meta } = action.payload || {};

      if (api?.success && api?.result) {
        const list = Array.isArray(api.result?.list) ? api.result.list : [];
        const totalCount = Number(api.result?.totalCount) || 0;

        state.roleList = list;
        state.roleTotal = totalCount;

        // use meta.limit always (since API doesn't return limit)
        const limit = Number(meta?.limit || state.roleLimit || 10) || 10;
        state.roleLimit = limit;

        state.rolePage = Number(meta?.page || state.rolePage || 1) || 1;
        state.roleTotalPages = Math.max(1, Math.ceil(totalCount / limit));
      } else {
        state.roleList = [];
        state.roleTotal = 0;
        state.roleTotalPages = 1;
      }
    });



    builder.addCase(createRole.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(createRole.fulfilled, (state) => {
      state.isBtnLoad = false;
    });
    builder.addCase(createRole.rejected, (state) => {
      state.isBtnLoad = false;
    });

    builder.addCase(updateRole.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(updateRole.fulfilled, (state) => {
      state.isBtnLoad = false;
    });
    builder.addCase(updateRole.rejected, (state) => {
      state.isBtnLoad = false;
    });

    builder.addCase(deleteRole.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(deleteRole.fulfilled, (state) => {
      state.isBtnLoad = false;
    });
    builder.addCase(deleteRole.rejected, (state) => {
      state.isBtnLoad = false;
    });

    // getRoleById returns data to UI; no state mutation required here
    builder.addCase(getRoleById.pending, (state) => {
      state.isSpin = true;
    });
    builder.addCase(getRoleById.fulfilled, (state) => {
      state.isSpin = false;
    });
    builder.addCase(getRoleById.rejected, (state) => {
      state.isSpin = false;
    });

    // ---------------- MENUS ----------------
    builder.addCase(getAllMenus.pending, (state) => {
      state.isMenuSpin = true;
    });
    builder.addCase(getAllMenus.fulfilled, (state, action) => {
      state.isMenuSpin = false;

      const payload = action.payload;

      if (payload?.success) {
        const list =
          (Array.isArray(payload?.result) && payload.result) ||
          (Array.isArray(payload?.result?.data) && payload.result.data) ||
          (Array.isArray(payload?.data) && payload.data) ||
          [];

        state.menuList = list;
      } else {
        state.menuList = [];
      }
    });
    builder.addCase(getAllMenus.rejected, (state) => {
      state.isMenuSpin = false;
      state.menuList = [];
    });
  },
});

export default userSlice.reducer;

export const {
  // existing exports
  setFormData,
  setFlag,
  setSearchText,
  setStatusFilter,
  setLimit,
  setPage,
  setDeleteUserId,
  clearDeleteUserId,

  // ✅ role exports (fixes your import error)
  setRoleFlag,
  setRoleFormData,
  setDeleteRoleId,
  clearDeleteRoleId,
  setRoleLimit,
  setRolePage,
  setUsertype
} = userSlice.actions;
